// Hour-bucket index for calendar events so the calendar grids can look up one
// hour's events without scanning the whole event list per hour-cell.
//
// Semantics mirror calendarEventStore.getEvents exactly: an event belongs to
// the hour its START falls in (multi-hour events appear only in their start
// hour), and a lookup window spans [start, start + 59min 59sec] inclusive.

export const HOUR_MS = 60 * 60 * 1000;

// Build Map<hourEpoch, [{ ev, startEpoch }]> from an event list. Events with
// an invalid/missing start are skipped (the legacy filter could never match
// them). Original array order is preserved inside each bucket.
export function buildHourIndex(events = []) {
    const index = new Map();
    for (let i = 0; i < events.length; i += 1) {
        const ev = events[i];
        const startEpoch = new Date(ev.start).getTime();
        if (Number.isNaN(startEpoch)) {
            continue;
        }
        const hourEpoch = Math.floor(startEpoch / HOUR_MS) * HOUR_MS;
        let bucket = index.get(hourEpoch);
        if (!bucket) {
            bucket = [];
            index.set(hourEpoch, bucket);
        }
        bucket.push({ ev, startEpoch });
    }
    return index;
}

// Look up the events starting inside the window that begins at
// windowStartEpoch. calendarIdSet must contain every accepted calendarId,
// including the '__all__' wildcard. Returns a fresh array holding the same
// event object references, in bucket (= original array) order.
export function lookupHourEvents(index, windowStartEpoch, calendarIdSet) {
    // Same end as the legacy code: start + 59 minutes + 59 seconds, computed
    // through Date so wall-clock arithmetic (DST) behaves identically.
    const end = new Date(windowStartEpoch);
    end.setMinutes(end.getMinutes() + 59);
    end.setSeconds(end.getSeconds() + 59);
    const windowEndEpoch = end.getTime();

    const events = [];
    const startBucket = Math.floor(windowStartEpoch / HOUR_MS) * HOUR_MS;
    const endBucket = Math.floor(windowEndEpoch / HOUR_MS) * HOUR_MS;
    for (let bucketKey = startBucket; bucketKey <= endBucket; bucketKey += HOUR_MS) {
        const bucket = index.get(bucketKey);
        if (!bucket) {
            continue;
        }
        for (let i = 0; i < bucket.length; i += 1) {
            const entry = bucket[i];
            if (entry.startEpoch >= windowStartEpoch
                && entry.startEpoch <= windowEndEpoch
                && calendarIdSet.has(entry.ev.calendarId)) {
                events.push(entry.ev);
            }
        }
    }
    return events;
}
